import { useEffect, useRef } from 'react'
import { useFunnel } from '../context/FunnelContext'
import { logger } from '../utils/logger'
import { localStore } from '../utils/localStorageKey'
import { config } from '../config/env'

// Set delay based on environment: 1 minute in test mode, 5 minutes in production
const ABANDON_DELAY_MS = config.testMode ? 1 * 60 * 1000 : 5 * 60 * 1000

// Key names for abandoned cart tracking
const TIMER_KEY = 'abandoned_cart_timer_id'
const START_TIME_KEY = 'abandoned_cart_start_time'
const CHECKOUT_LOGGED_KEY = 'abandoned_cart_checkout_logged'

// Global flag to prevent multiple instances from logging simultaneously
// This is shared across all hook instances (product selection + payment pages)
let globalLogInProgress = false

// IMPORTANT: Use localStorage (not sessionStorage) for persistence across browser sessions
// This ensures timer resumes even if user closes browser completely

export const useAbandonedCartTracking = (isCheckoutPage = false) => {
  const { funnelData, funnelConfig: config, activeFunnelId } = useFunnel()
  const timerRef = useRef(null)
  const hasLoggedStartRef = useRef(false)
  const funnelDataRef = useRef(funnelData)
  const funnelIdRef = useRef(null)

  useEffect(() => {
    funnelIdRef.current = funnelData.funnel_id || activeFunnelId || config?.funnel_id || null
  }, [funnelData.funnel_id, activeFunnelId, config?.funnel_id])

  // Check localStorage on mount to prevent duplicate logs across page navigations
  useEffect(() => {
    const checkoutLogged = localStore.getItem(CHECKOUT_LOGGED_KEY, config)
    if (checkoutLogged) {
      hasLoggedStartRef.current = true
      logger.debug(`[Abandoned Cart] Found existing checkout log, marking as logged`)
    }
  }, [config])
  
  // Keep funnelDataRef up to date
  useEffect(() => {
    funnelDataRef.current = funnelData
  }, [funnelData])

  // Keep funnel_id ref up to date with the best available source.
  // funnelData.funnel_id is set inside setFunnelConfig but that effect can race
  // with the checkout page mount (notably on variant-cv-1), so fall back to
  // activeFunnelId / config.funnel_id to guarantee the payload always carries it.
  useEffect(() => {
    funnelIdRef.current =
      funnelData.funnel_id || activeFunnelId || config?.funnel_id || null
  }, [funnelData.funnel_id, activeFunnelId, config?.funnel_id])

  // Log checkout started with retry logic
  const logCheckoutStarted = async (retries = 3) => {
    // CRITICAL: Check multiple deduplication flags
    if (hasLoggedStartRef.current || localStore.getItem(CHECKOUT_LOGGED_KEY, config) || globalLogInProgress) {
      logger.debug(`[Abandoned Cart] Checkout already logged or in progress, skipping`)
      return
    }
    
    // Set global flag to prevent simultaneous calls from other hook instances
    globalLogInProgress = true
    
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        // Use funnelData from ref to get the LATEST state
        // This ensures we get the most up-to-date values including highest_step_reached
        const userData = funnelDataRef.current

        // Validate required fields before sending
        if (!userData.entry_id || !userData.email) {
          logger.warn('[Abandoned Cart] Missing required fields (entry_id or email), skipping for now')
          return
        }

        logger.debug('[Abandoned Cart] Logging checkout with highest_step:', {
          key_name: userData.highest_step_reached_key_name,
          index: userData.highest_step_reached_index
        })

        // Resolve funnel_id with fallbacks so the field is never empty even if
        // funnelData.funnel_id hasn't been hydrated yet from setFunnelConfig.
        const resolvedFunnelId =
          userData.funnel_id || funnelIdRef.current || activeFunnelId || config?.funnel_id || ''

        // Prepare payload with all required fields
        const payload = {
          entry_id: userData.entry_id,
          funnel_id: resolvedFunnelId,
          affid: userData.affid || '',
          email: userData.email,
          phone: userData.phone || '',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          state: userData.state || '',
          gender: userData.gender || '',
          weight: userData.weight || '',
          goal_weight: userData.goal_weight || '',
          lose_weight: userData.lose_weight || '',
          bmi: userData.bmi || '',
          date_of_birth: userData.date_of_birth || '',
          height_inches: userData.height_inches || '',
          height_feet: userData.height_feet || '',
          product_select: userData.product_select || '',
          selected_product_id: userData.selected_product_id || '',
          selected_product_name: userData.selected_product_name || '',
          selected_product_price: userData.selected_product_price || '',
          selected_product_bundle_id: userData.selected_product_bundle_id || '',
          selected_product_type: userData.selected_product_type || '',
          selected_product_medication: userData.selected_product_medication || '',
          selected_plan: userData.selected_plan || '',
          chosen_medicine_name: userData.chosen_medicine_name || '',
          chosen_medicine_price: userData.chosen_medicine_price || '',
          sms_subscription_checkbox: userData.sms_subscription_checkbox ? 1 : 0,
          highest_step_reached_key_name: userData.highest_step_reached_key_name || '',
          highest_step_reached_index: userData.highest_step_reached_index || 0,
          case_id: userData.case_id || null,
          ef_tid: userData.ef_tid || null,
          everflow_offer_id: userData.everflow_offer_id || null,
          promo_code: userData.promo_code || null,
          promo_id: userData.promo_id || null,
          promo_percent_off: userData.promo_percent_off || null,
          promo_amount_off: userData.promo_amount_off || null,  
          unique_effects: userData.unique_effects_options || null,
          how_you_sleep: userData.how_you_sleep_options || null,
          priority: userData.priority_options_choices || null,
        }

        const response = await fetch('/funnel-checkout-started', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        
        const result = await response.json()
        logger.debug('[Abandoned Cart] Checkout started logged:', result)
        
        // Mark as logged (both in-memory and localStorage)
        hasLoggedStartRef.current = true
        localStore.setItem(CHECKOUT_LOGGED_KEY, 'true', config)
        localStore.setItem(START_TIME_KEY, Date.now().toString(), config)
        logger.debug(`[Abandoned Cart] Checkout logged successfully`)
        globalLogInProgress = false
        return // Success - exit retry loop
        
      } catch (error) {
        logger.error(`[Abandoned Cart] Error logging checkout started (attempt ${attempt}/${retries}):`, error)
        
        if (attempt < retries) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)))
        } else {
          logger.error('[Abandoned Cart] Failed to log checkout started after all retries')
          globalLogInProgress = false // Reset flag even on failure
        }
      }
    }
    
    globalLogInProgress = false // Reset flag if all retries fail
  }

  // Log abandoned cart with retry logic
  const logAbandonedCart = async (retries = 3) => {
    // Guard: clearTimer() removes START_TIME_KEY when payment completes.
    // If it's gone, the user has already paid — do not log an abandoned cart.
    if (!localStore.getItem(START_TIME_KEY, config)) {
      logger.debug('[Abandoned Cart] Timer was cleared (payment completed), skipping abandoned cart log')
      return
    }

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        // Use ref to get latest data (important for timer callbacks)
        const userData = funnelDataRef.current
        
        // Validate entry_id exists
        if (!userData?.entry_id) {
          logger.error('[Abandoned Cart] Cannot log abandoned cart: entry_id is missing')
          clearTimer()
          return
        }
        
        // Only need to send entry_id - all data was already stored in checkout_started
        const response = await fetch('/funnel-abandoned-cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entry_id: userData.entry_id
          })
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        
        const result = await response.json()
        logger.debug('[Abandoned Cart] Abandoned cart logged:', result)
        
        clearTimer()
        return // Success - exit retry loop
        
      } catch (error) {
        logger.error(`[Abandoned Cart] Error logging abandoned cart (attempt ${attempt}/${retries}):`, error)
        
        if (attempt < retries) {
          // Exponential backoff: 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)))
        } else {
          logger.error('[Abandoned Cart] Failed to log abandoned cart after all retries')
          // Still clear timer to prevent infinite retries
          clearTimer()
        }
      }
    }
  }

  // Start timer
  const startTimer = () => {
    clearTimer()
    
    const timerId = setTimeout(() => {
      logger.debug('[Abandoned Cart] 5 minutes elapsed - logging abandoned cart')
      logAbandonedCart()
    }, ABANDON_DELAY_MS)
    
    timerRef.current = timerId
    localStore.setItem(TIMER_KEY, timerId.toString(), config)
    
    logger.debug(`[Abandoned Cart] Timer started (5 minutes)`)
  }

  // Clear timer
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    localStore.removeItem(TIMER_KEY, config)
    localStore.removeItem(START_TIME_KEY, config)
    localStore.removeItem(CHECKOUT_LOGGED_KEY, config)
    hasLoggedStartRef.current = false
    
    logger.debug(`[Abandoned Cart] Timer cleared`)
  }

  // Resume timer if needed
  const resumeTimer = () => {
    const startTime = localStore.getItem(START_TIME_KEY, config)
    if (!startTime) return
    
    const elapsed = Date.now() - parseInt(startTime)
    
    if (elapsed >= ABANDON_DELAY_MS) {
      // Already past 5 minutes
      logger.debug('[Abandoned Cart] Resuming - already past 5 minutes')
      logAbandonedCart()
    } else {
      // Resume with remaining time
      const remaining = ABANDON_DELAY_MS - elapsed
      logger.debug(`[Abandoned Cart] Resuming timer with ${Math.round(remaining / 1000 / 60)} minutes remaining`)
      
      const timerId = setTimeout(() => {
        logAbandonedCart()
      }, remaining)
      
      timerRef.current = timerId
      localStore.setItem(TIMER_KEY, timerId.toString(), config)
    }
  }

  // Effect: Handle checkout page entry
  useEffect(() => {
    if (isCheckoutPage) {
      // Check if timer already running and if checkout was already logged
      const existingTimer = localStore.getItem(TIMER_KEY, config)
      const checkoutLogged = localStore.getItem(CHECKOUT_LOGGED_KEY, config)
      
      if (checkoutLogged) {
        // Already logged, just resume timer if needed
        if (existingTimer) {
          logger.debug(`[Abandoned Cart] Checkout already logged, resuming timer`)
          resumeTimer()
        }
        // Don't call logCheckoutStarted() again!
      } else {
        // First time - start timer but delay logging until data is ready
        logger.debug(`[Abandoned Cart] Checkout page reached, starting timer...`)
        startTimer()
        
        // Check if required product/plan data is already available
        const hasRequiredData = funnelDataRef.current.selected_product_id && funnelDataRef.current.selected_plan
        
        if (hasRequiredData) {
          // Data is ready, log immediately
          logger.debug('[Abandoned Cart] Product/plan data ready, logging immediately')
          setTimeout(() => {
            logCheckoutStarted()
          }, 500) // Small delay to ensure state is updated
        } else {
          // Data not ready yet - will be logged when parent calls logCheckoutStarted() manually
          logger.debug('[Abandoned Cart] Product/plan data not ready, waiting for manual trigger')
        }
      }
    }
    
    // Cleanup on unmount
    return () => {
      // Don't clear timer on unmount - let it persist
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckoutPage, config])
  
  // Effect: Watch for product/plan selection changes and auto-log if not already logged
  useEffect(() => {
    // Triple-check: hasLoggedStartRef, localStorage, and globalLogInProgress
    if (isCheckoutPage && !hasLoggedStartRef.current && !localStore.getItem(CHECKOUT_LOGGED_KEY, config) && !globalLogInProgress) {
      const hasRequiredData = funnelData.selected_product_id && funnelData.selected_plan
      
      if (hasRequiredData) {
        logger.debug(`[Abandoned Cart] Product/plan data now available, auto-logging checkout started`)
        logCheckoutStarted()
      }
    }
  }, [funnelData.selected_product_id, funnelData.selected_plan, isCheckoutPage, config])

  return {
    clearTimer, // Export for payment success page
    logCheckoutStarted, // Export for manual triggering when data is ready
  }
}
